import { IFindingSpan } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/graph-finding-spans.interface';
import { getFindingSpansCoverage } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/graph-finding-spans.util';
import { EFindingCardSortMode, IFindingCardData, IFindingsSummary } from './finding-card.interface';

export function sortFindingCards<T extends IFindingCardData>(cards: T[], sortMode: EFindingCardSortMode): T[] {
  const byTime = (a: T, b: T): number => a.sortTime - b.sortTime;

  if (sortMode === EFindingCardSortMode.longestVisible) {
    return [...cards].sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0) || byTime(a, b));
  }

  return [...cards].sort(byTime);
}

export function numberFindingCardsChronologically<T extends IFindingCardData>(cards: T[]): T[] {
  return sortFindingCards(cards, EFindingCardSortMode.time).map((card, index) => ({ ...card, number: index + 1 }));
}

// Card after the selected one, wrapping around; the first card when nothing (or an unknown id) is selected.
export function getNextFindingCardId(cards: IFindingCardData[], selectedId: string | null): string | null {
  if (!cards.length) {
    return null;
  }

  const selectedIndex = cards.findIndex((card) => card.id === selectedId);

  return cards[(selectedIndex + 1) % cards.length].id;
}

export function formatFindingDuration(seconds: number | null | undefined): string | null {
  if (!seconds || seconds <= 0) {
    return null;
  }

  const totalSeconds = Math.round(seconds);

  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }

  if (totalSeconds < 3600) {
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;

    return remainingSeconds ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

// Visible time from the graph spans of the active cards, overlaps counted once; `spanUnitsPerSecond` is 1000
// on streams (ms axis) and 1 on posts.
export function summarizeFindings(
  activeCards: IFindingCardData[],
  spans: IFindingSpan[],
  spanUnitsPerSecond: number,
): IFindingsSummary {
  const activeIds = new Set(activeCards.map((card) => card.id));
  const coveredUnits = getFindingSpansCoverage(spans.filter((span) => activeIds.has(span.id)));

  return { count: activeCards.length, duration: formatFindingDuration(coveredUnits / spanUnitsPerSecond) };
}
